/**
 * FilterSidebar Component - Story 7.1: Create Marketplace Homepage
 * Comprehensive filtering interface for product browsing
 *
 * Features:
 * - Multi-select category checkboxes with icons and counts
 * - Location filtering (state and district dropdowns)
 * - Clear filters functionality
 * - Responsive design (sidebar on desktop, collapsible on mobile)
 * - Debounced filter changes to prevent excessive API calls
 * - Loading state support
 * - Full accessibility (ARIA labels, keyboard navigation)
 * - Visual feedback for active filters
 *
 * @component
 * @example
 * // Basic usage
 * <FilterSidebar
 *   filters={{ category: 'BIOGAS', state: 'Maharashtra', district: null, page: 1, pageSize: 12 }}
 *   onFiltersChange={(newFilters) => setFilters({ ...filters, ...newFilters })}
 *   states={stateOptions}
 *   districts={districtOptions}
 * />
 *
 * @example
 * // With loading state and category counts
 * <FilterSidebar
 *   filters={filters}
 *   onFiltersChange={handleFiltersChange}
 *   states={stateOptions}
 *   districts={districtOptions}
 *   isLoading={isLoadingProducts}
 *   className="sticky top-4"
 * />
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { X, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  FilterSidebarProps,
  ProductBrowseFilters,
  LocationOption,
} from '../types/publicMarketplace.types';
import type { ProductCategory } from '../types';
import { getAllCategories, getCategoryMetadata } from '../config/categoryConfig';

/**
 * Debounce delay for filter changes (in milliseconds)
 * Prevents excessive API calls while user is still adjusting filters
 */
const DEBOUNCE_DELAY = 300;

/**
 * FilterSidebar Component
 * Main component for product filtering interface
 */
export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onFiltersChange,
  states,
  districts,
  isLoading = false,
  className,
}) => {
  // Local state for selected categories (supports multi-select)
  const [selectedCategories, setSelectedCategories] = useState<Set<ProductCategory>>(
    new Set(filters.category ? [filters.category] : [])
  );

  // Local state for location filters
  const [selectedState, setSelectedState] = useState<string | null>(filters.state || null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(
    filters.district || null
  );

  // Get all categories, memoized
  const categories = useMemo(() => getAllCategories(), []);

  // Debounce timer ref
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  /**
   * Debounced filter change handler
   * Delays the actual filter update to prevent excessive API calls
   */
  const debouncedFilterChange = useCallback(
    (updates: Partial<ProductBrowseFilters>) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onFiltersChange({ ...updates, page: 1 }); // Reset to page 1 when filters change
      }, DEBOUNCE_DELAY);
    },
    [onFiltersChange]
  );

  /**
   * Handle category checkbox toggle
   * Supports multi-select functionality
   */
  const handleCategoryToggle = useCallback(
    (category: ProductCategory, checked: boolean) => {
      setSelectedCategories((prev) => {
        const updated = new Set(prev);
        if (checked) {
          updated.add(category);
        } else {
          updated.delete(category);
        }

        // For now, we only support single category selection
        // If multiple categories are selected, use the last one
        // TODO: Update API to support multiple categories
        const categoryValue = updated.size > 0 ? Array.from(updated)[0] : null;
        debouncedFilterChange({ category: categoryValue });

        // For single-select behavior, clear others
        if (checked) {
          return new Set([category]);
        }
        return updated;
      });
    },
    [debouncedFilterChange]
  );

  /**
   * Handle state selection change
   * Also clears district when state changes
   */
  const handleStateChange = useCallback(
    (state: string) => {
      setSelectedState(state);
      setSelectedDistrict(null); // Clear district when state changes
      debouncedFilterChange({ state, district: null });
    },
    [debouncedFilterChange]
  );

  /**
   * Handle district selection change
   */
  const handleDistrictChange = useCallback(
    (district: string) => {
      setSelectedDistrict(district);
      debouncedFilterChange({ district });
    },
    [debouncedFilterChange]
  );

  /**
   * Clear all filters
   * Resets to default state
   */
  const handleClearFilters = useCallback(() => {
    setSelectedCategories(new Set());
    setSelectedState(null);
    setSelectedDistrict(null);
    onFiltersChange({ category: null, state: null, district: null, page: 1 });
  }, [onFiltersChange]);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return selectedCategories.size > 0 || selectedState !== null || selectedDistrict !== null;
  }, [selectedCategories, selectedState, selectedDistrict]);

  /**
   * Sync local state with prop changes
   * Ensures component reflects external filter updates
   */
  useEffect(() => {
    setSelectedCategories(new Set(filters.category ? [filters.category] : []));
    setSelectedState(filters.state || null);
    setSelectedDistrict(filters.district || null);
  }, [filters.category, filters.state, filters.district]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Card
      className={cn(
        'w-full md:w-80 lg:w-96',
        'border-border bg-card',
        'transition-opacity duration-200',
        isLoading && 'opacity-70 pointer-events-none',
        className
      )}
      role="complementary"
      aria-label="Product filters"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <CardTitle className="text-lg font-semibold">Filters</CardTitle>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              disabled={isLoading}
              className="h-8 gap-1.5 text-xs hover:bg-destructive/10 hover:text-destructive"
              aria-label="Clear all filters"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>

        {/* Active Filters Count */}
        {hasActiveFilters && (
          <div className="mt-2">
            <Badge variant="secondary" className="text-xs">
              {selectedCategories.size +
                (selectedState ? 1 : 0) +
                (selectedDistrict ? 1 : 0)}{' '}
              active filter{hasActiveFilters ? 's' : ''}
            </Badge>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Category Filter Section */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">
            Category
          </Label>

          <div className="space-y-2.5" role="group" aria-label="Product categories">
            {categories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategories.has(category.key);

              return (
                <div
                  key={category.key}
                  className={cn(
                    'flex items-center gap-3 rounded-md border px-3 py-2.5',
                    'transition-all duration-150',
                    'hover:bg-accent/50 hover:border-accent-foreground/20',
                    isSelected && 'bg-accent border-accent-foreground/30',
                    isLoading && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Checkbox
                    id={`category-${category.key}`}
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      handleCategoryToggle(category.key, checked as boolean)
                    }
                    disabled={isLoading}
                    aria-label={category.ariaLabel}
                  />

                  <label
                    htmlFor={`category-${category.key}`}
                    className="flex flex-1 items-center gap-2 cursor-pointer"
                  >
                    <Icon
                      className={cn('h-4 w-4 flex-shrink-0', category.color)}
                      aria-hidden="true"
                    />
                    <span className="text-sm font-medium text-foreground">
                      {category.label}
                    </span>
                  </label>

                  {/* Optional: Product count badge
                  {counts?.[category.key] !== undefined && (
                    <Badge variant="outline" className="text-xs">
                      {counts[category.key]}
                    </Badge>
                  )} */}
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Location Filter Section */}
        <div className="space-y-4">
          <Label className="text-sm font-semibold text-foreground">
            Location
          </Label>

          {/* State Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="state-select" className="text-xs text-muted-foreground">
              State
            </Label>
            <Select
              value={selectedState || ''}
              onValueChange={handleStateChange}
              disabled={isLoading || states.length === 0}
            >
              <SelectTrigger
                id="state-select"
                className="w-full"
                aria-label="Select state"
              >
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {states.length === 0 ? (
                  <div className="py-2 px-3 text-sm text-muted-foreground">
                    No states available
                  </div>
                ) : (
                  states.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* District Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="district-select" className="text-xs text-muted-foreground">
              District
            </Label>
            <Select
              value={selectedDistrict || ''}
              onValueChange={handleDistrictChange}
              disabled={isLoading || !selectedState || districts.length === 0}
            >
              <SelectTrigger
                id="district-select"
                className="w-full"
                aria-label="Select district"
              >
                <SelectValue
                  placeholder={
                    !selectedState
                      ? 'Select state first'
                      : districts.length === 0
                      ? 'No districts available'
                      : 'Select district'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {districts.length === 0 ? (
                  <div className="py-2 px-3 text-sm text-muted-foreground">
                    {!selectedState
                      ? 'Please select a state first'
                      : 'No districts available for this state'}
                  </div>
                ) : (
                  districts.map((district) => (
                    <SelectItem key={district.value} value={district.value}>
                      {district.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Clear Location Button */}
          {(selectedState || selectedDistrict) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedState(null);
                setSelectedDistrict(null);
                onFiltersChange({ state: null, district: null, page: 1 });
              }}
              disabled={isLoading}
              className="w-full text-xs"
              aria-label="Clear location filters"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear Location
            </Button>
          )}
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span>Updating filters...</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Display name for debugging
FilterSidebar.displayName = 'FilterSidebar';

export default FilterSidebar;
